import useApi from "./useApi";
import * as Sentry from "@sentry/react";
import useI18n from "./useI18n";
import useEnv from "./useEnv";
import { useContext } from "react";
import { JobContext } from "../contexts/JobContext";

const useWhiteLabeling = (job) => {
    const { getJson, getBlob } = useApi();
    const { getLocale } = useI18n();
    const { getEnv } = useEnv();
    const logoMaxHeightSetting = getEnv('WHITELABEL_LOGO_MAX_HEIGHT');
    const LOGO_MAX_HEIGHT = logoMaxHeightSetting !== undefined && logoMaxHeightSetting !== ''
        ? parseInt(logoMaxHeightSetting, 10)
        : 56;
    const { whiteLabeling, updateWhiteLabeling } = useContext(JobContext);
    const language = getLocale();
    const cid = job?.cid ?? null;
    const jobid = job?.jobid ?? null;
    const key = `${language}-${cid}-${jobid}`;
    const whiteLabelData = whiteLabeling[key] ?? null;

    /**
     * Fetches the white labeling data from the API
     * @returns {Promise<null|*>}
     */
    const fetchWhiteLabelingData = async () => {
        if (language === null || cid === null || jobid === null) {
            return null;
        }
        // Check context cache first
        if (whiteLabeling[key] !== undefined) {
            return whiteLabeling[key];
        }
        const queryParams = `?language=${language}&cid=${cid}&jobid=${jobid}`;
        try {
            const data = await getJson(`/online/whitelabel/getdata${queryParams}`);
            updateWhiteLabeling(key, data?.data ?? null);
            return data?.data ?? null;
        } catch (error) {
            Sentry.captureException(error);
            return null;
        }
    };
    
    /**
     * Fetches the image data from the API
     * @param {number|string|null|undefined} assetId
     * @returns {Promise<null|string>}
     */
    const fetchWhiteLabelingImageData = async (assetId) => {
        if (assetId === undefined || assetId === null || parseInt(assetId) === 0) {
            return null;
        }
        // Check context cache first
        if (whiteLabeling[assetId] !== undefined) {
            return whiteLabeling[assetId];
        }
        try {
            const data = await getBlob(`/online/whitelabel/getimagedata?asset_id=${assetId}`, 'image/*');
            if (data === null) {
                return null;
            }
            const url = URL.createObjectURL(data);
            updateWhiteLabeling(assetId, url);
            return url;
        } catch (error) {
            Sentry.captureException(error);
            return null;
        }
    };

    const resolveBrandingSettings = () => {
        const settings = {
            buttonColor: null,
            buttonTextColor: null,
            logoWidth: null,
            logoHeight: null
        }
        if (whiteLabelData?.brandingSettings !== undefined && whiteLabelData?.brandingSettings !== null) {
            const brandingSettings = whiteLabelData.brandingSettings;
            settings.buttonColor = brandingSettings.button_color;
            settings.buttonTextColor = brandingSettings.button_text_color;
            if (brandingSettings.logo_width && brandingSettings.logo_height) {
                const aspectRatio = brandingSettings.logo_width / brandingSettings.logo_height;
                const height = LOGO_MAX_HEIGHT > 0
                    ? Math.min(brandingSettings.logo_height, LOGO_MAX_HEIGHT)
                    : brandingSettings.logo_height;
                settings.logoWidth = height * aspectRatio;
                settings.logoHeight = height;
            }
        }
        return settings;
    }
    
    return {
        fetchWhiteLabelingData,
        fetchWhiteLabelingImageData,
        whiteLabelData,
        brandingSettings: resolveBrandingSettings()
    };
};

export default useWhiteLabeling;
